import { useRef } from 'react'
import styles from './PPTBox.module.less'
import OverallPptPage from './PptPage/OverallPptPage/OverallPptPage'
import { SliderModels } from '@/types/slider'
import PeriodPptPage from './PptPage/PeriodPptPage/PeriodPptPage'
import { dimensionMetricsPages } from '@/components/PDFDownloadBtn/PDFBox/PDFBox'
import { genPx, replaceFontSize } from './doPPTExport'
import HeatMapPptPage from './PptPage/HeatMapPptPage/HeatMapPptPage'
import cls from 'classnames'
import { getFontFace } from '@/helpers/formatHTMLUtils'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'

const PORTRAIT_HEATMAPS_PER_PAGE = 14
const DEFAULT_HEATMAPS_PER_PAGE = 12

const getUniqueHeatMapCount = (project: ProjectModels.ProjectDetail) => {
  return project.detailMetrics.reduce((count, detail, index, list) => {
    const second = Number(detail.secondCount)
    if (!Number.isFinite(second)) {
      return count
    }
    return list.findIndex((item) => Number(item.secondCount) === second) === index ? count + 1 : count
  }, 0)
}

const genTotalCnt = (project: ProjectModels.ProjectDetail) => {
  /** 介绍 2 页 */
  const introductionPages = 2
  /** 基础信息：封面 + 基础信息 2 页 */
  const part1Pages = 2
  /** 总览: 封面 + 柱状图 + 折线图 + Summary + advantag/disAdvantage + Suggestions */
  const part2Pages = 1 + 5
  /** 时刻分析: 封面 +  时刻片段 + ( Summary + Advantage/Disadvantage + Suggestions ) * 切片的数量 */
  const part3Pages =
    1 +
    dimensionMetricsPages('firstThree', project) +
    dimensionMetricsPages('keyInformation', project) +
    dimensionMetricsPages('brandConnection', project) +
    dimensionMetricsPages('interestingEnagement', project) +
    dimensionMetricsPages('lastThree', project)
  const heatMapsPerPage = isPortraitByProjectInfo(project) ? PORTRAIT_HEATMAPS_PER_PAGE : DEFAULT_HEATMAPS_PER_PAGE
  const uniqueHeatMapCount = getUniqueHeatMapCount(project)
  /** 热力图: 封面 + 图片 + 结尾 */
  const part4Pages = 1 + Math.ceil(uniqueHeatMapCount / heatMapsPerPage) + 1
  /** 总的页数 */
  return {
    introCnt: introductionPages,
    part1Cnt: part1Pages,
    part2Cnt: part2Pages,
    part3Cnt: part3Pages,
    part4Cnt: part4Pages,
    total: introductionPages + part1Pages + part2Pages + part3Pages + part4Pages,
  }
}

function PPTBox(props: {
  onFinished: (sliders: SliderModels.Slider[]) => void
  onProgress: (total: number, percent: number) => void
  project: ProjectModels.ProjectDetail
  debug?: boolean
}) {
  const fontFace = getFontFace().key
  const { introCnt, part1Cnt, part2Cnt, part3Cnt, total } = genTotalCnt(props.project)
  const totalSliders = useRef<Map<number, SliderModels.Slider>>(new Map())
  const finishedRef = useRef(false)

  const handleSliderReady = (slider: SliderModels.Slider, no: number) => {
    if (finishedRef.current) {
      return
    }
    totalSliders.current.set(no, slider)
    const percent = Math.floor((totalSliders.current.size / total) * 100)
    // console.log(part1Cnt, part2Cnt, part3Cnt, part4Cnt)
    // console.log(
    //   `total: ${total}, now: ${totalSliders.current.length},percent: ${Math.floor((totalSliders.current.length / total) * 100)}%`
    // )
    // const _totalSliders = totalSliders.current.sort((a, b) => a.no - b.no)
    // console.log('totalSliders.current', _totalSliders)
    props.onProgress(total, percent)
    if (totalSliders.current.size === total) {
      finishedRef.current = true
      const sortedSliders = Array.from(totalSliders.current.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([no, slider]) => ({ slider, no }))
      // 这里将长度转换一下
      const _sliders = sortedSliders.map((item) => {
        const { elements } = item.slider
        const _elements = elements.map((element) => {
          const { x, y } = element
          return {
            ...element,
            x: genPx(x),
            y: genPx(y),
            width: genPx(element.width),
            ...(
              element.type === 'line'
                ? { end: [genPx(element.end[0]), genPx(element.end[1])] as [number, number] }
                : { height: genPx(element.height) }
            ),
            ...(element.type === 'text' ? { content: replaceFontSize(element.content) } : {}),
          }
        })
        return { slider: { ...item.slider, elements: _elements }, no: item.no }
      })
      totalSliders.current = new Map(_sliders.map((item) => [item.no, item.slider]))
      props.onFinished(_sliders.map((item) => item.slider))
    }
  }

  return (
    <div className={cls(styles.pptBox, { [styles.debug]: props.debug }, styles[fontFace])}>
      <OverallPptPage project={props.project} baseNo={0} onSliderReady={handleSliderReady} />
      <PeriodPptPage
        project={props.project}
        baseNo={introCnt + part1Cnt + part2Cnt}
        onSliderReady={handleSliderReady}
      />
      <HeatMapPptPage
        project={props.project}
        baseNo={introCnt + part1Cnt + part2Cnt + part3Cnt}
        onSliderReady={handleSliderReady}
      />
    </div>
  )
}

export default PPTBox
